import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiX, FiSave, FiTrendingUp, FiTrendingDown, FiInfo, FiLayers, FiCalendar, FiEdit3 } = FiIcons

const TradeForm = ({ trade, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    symbol: '',
    side: 'buy',
    entry_price: '',
    exit_price: '',
    quantity: '',
    entry_time: new Date().toISOString().slice(0, 16),
    setup_type: 'breakout',
    notes: '',
    pnl: ''
  })

  useEffect(() => {
    if (trade) {
      setFormData({
        symbol: trade.symbol || '',
        side: trade.side || 'buy',
        entry_price: trade.entry_price?.toString() || '',
        exit_price: trade.exit_price?.toString() || '',
        quantity: trade.quantity?.toString() || '',
        entry_time: trade.entry_time ? new Date(trade.entry_time).toISOString().slice(0, 16) : '',
        setup_type: trade.setup_type || 'breakout',
        notes: trade.notes || '',
        pnl: trade.pnl?.toString() || ''
      })
    }
  }, [trade])

  const handleSubmit = (e) => {
    e.preventDefault()
    const entry = parseFloat(formData.entry_price)
    const exit = parseFloat(formData.exit_price)
    const qty = parseFloat(formData.quantity)
    
    let calculatedPnl = parseFloat(formData.pnl)
    if (!isNaN(entry) && !isNaN(exit) && !isNaN(qty)) {
      calculatedPnl = formData.side === 'buy' ? (exit - entry) * qty : (entry - exit) * qty
    }

    onSubmit({
      ...formData,
      pnl: calculatedPnl || 0
    })
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-950/90 backdrop-blur-xl flex justify-center items-start sm:items-center p-4 z-[100] overflow-y-auto"
    >
      <motion.div 
        initial={{ scale: 0.95, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white dark:bg-[#0b0f1a] rounded-3xl md:rounded-[2.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] max-w-2xl w-full my-4 sm:my-8 border border-gray-200 dark:border-white/10 overflow-hidden relative"
      >
        {/* Top Gradient Bar */}
        <div className="h-2 w-full bg-gradient-to-r from-lux-primary via-lux-accent to-purple-500"></div>

        <div className="p-6 sm:p-10">
          <header className="flex justify-between items-center mb-8 sm:mb-10">
            <div className="space-y-1">
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                {trade ? 'Modify Entry' : 'Record Trade'}
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] sm:tracking-[0.3em]">
                {trade ? 'Update existing record' : 'Execute new entry'}
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 sm:p-3 bg-gray-100 dark:bg-white/5 hover:bg-red-500/10 hover:text-red-500 rounded-xl sm:rounded-2xl transition-all duration-300 group"
            >
              <SafeIcon icon={FiX} className="h-5 w-5 transition-transform group-hover:rotate-90" />
            </button>
          </header>

          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* Section 1: Asset Selection */}
            <div className="bg-gray-50/50 dark:bg-white/[0.02] p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 dark:border-white/5 space-y-6">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiInfo} className="h-4 w-4 text-lux-primary" />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Asset & Direction</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Symbol</label>
                  <div className="relative group">
                    <input 
                      type="text" 
                      value={formData.symbol}
                      onChange={(e) => handleChange('symbol', e.target.value)}
                      className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600"
                      placeholder="BTC/USDT"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Side</label>
                  <div className="flex p-1.5 bg-gray-100 dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-white/5">
                    <button
                      type="button"
                      onClick={() => handleChange('side', 'buy')}
                      className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl text-[10px] font-black transition-all duration-300 ${
                        formData.side === 'buy' 
                          ? 'bg-green-500 text-white shadow-lg shadow-green-500/20 scale-[1.02]' 
                          : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                      }`}
                    >
                      <SafeIcon icon={FiTrendingUp} className="h-3.5 w-3.5" />
                      <span>LONG</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('side', 'sell')}
                      className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl text-[10px] font-black transition-all duration-300 ${
                        formData.side === 'sell' 
                          ? 'bg-red-500 text-white shadow-lg shadow-red-500/20 scale-[1.02]' 
                          : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                      }`}
                    >
                      <SafeIcon icon={FiTrendingDown} className="h-3.5 w-3.5" />
                      <span>SHORT</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Financial Metrics */}
            <div className="bg-gray-50/50 dark:bg-white/[0.02] p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 dark:border-white/5 space-y-6">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiLayers} className="h-4 w-4 text-lux-accent" />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Entry Details</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Entry Price</label>
                  <input 
                    type="number" step="any"
                    value={formData.entry_price}
                    onChange={(e) => handleChange('entry_price', e.target.value)}
                    className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Exit Price</label>
                  <input 
                    type="number" step="any"
                    value={formData.exit_price}
                    onChange={(e) => handleChange('exit_price', e.target.value)}
                    className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Quantity</label>
                  <input 
                    type="number" step="any"
                    value={formData.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white"
                    placeholder="1.0"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Context & Strategy */}
            <div className="bg-gray-50/50 dark:bg-white/[0.02] p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-gray-100 dark:border-white/5 space-y-6">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiCalendar} className="h-4 w-4 text-purple-500" />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Context & Strategy</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Setup Type</label>
                  <div className="relative">
                    <select 
                      value={formData.setup_type}
                      onChange={(e) => handleChange('setup_type', e.target.value)}
                      className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white appearance-none"
                    >
                      <option value="breakout">Breakout</option>
                      <option value="pullback">Pullback</option>
                      <option value="reversal">Reversal</option>
                      <option value="trend_continuation">Trend Continuation</option>
                      <option value="scalp">Scalp</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-400">
                      <SafeIcon icon={FiIcons.FiChevronDown} className="h-4 w-4" />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-tighter">Entry Timestamp</label>
                  <input 
                    type="datetime-local" 
                    value={formData.entry_time}
                    onChange={(e) => handleChange('entry_time', e.target.value)}
                    className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between ml-1">
                  <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-tighter">Analysis Rationale</label>
                  <SafeIcon icon={FiEdit3} className="h-3 w-3 text-gray-300" />
                </div>
                <textarea 
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows="3"
                  className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-gray-100 dark:border-white/5 focus:border-lux-primary dark:focus:border-lux-primary outline-none transition-all shadow-sm text-sm font-bold text-gray-900 dark:text-white resize-y min-h-[100px]"
                  placeholder="What was the core reason for this execution?"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button 
                type="button" 
                onClick={onClose}
                className="order-2 sm:order-1 flex-1 py-4 sm:py-5 rounded-2xl sm:rounded-3xl text-xs font-black text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-all uppercase tracking-widest border border-transparent hover:border-gray-200 dark:hover:border-white/10"
              >
                Discard
              </button>
              <button 
                type="submit"
                className="order-1 sm:order-2 flex-[2] bg-gradient-to-r from-lux-primary to-lux-accent hover:opacity-90 text-white py-4 sm:py-5 rounded-2xl sm:rounded-3xl flex items-center justify-center space-x-3 shadow-xl shadow-lux-primary/20 transition-all active:scale-95"
              >
                <SafeIcon icon={FiSave} className="h-5 w-5" />
                <span className="text-sm font-bold tracking-tight uppercase">
                  {trade ? 'Update Record' : 'Commit Entry'}
                </span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default TradeForm